/**
 * Users admin page filter/sort — pure predicate + comparator (issue #1224).
 *
 * `/admin/users` fetches the full platform user list in one REST call
 * (`useUsers()` -> `GET /users`, still a full `UserRepository.listAll()`
 * GSI1 scan server-side — see PR description for why this stayed
 * client-side rather than adding server-side query params). This module
 * extracts the filter (role + team + email/name search, AND-composed) and
 * sort (by visible column) logic into pure, independently testable
 * functions so `UsersAdminPage` only wires state to them.
 */

import type { PlatformUser, UserRole } from '../types/users';
import type { Team } from '../graphql/types';

/** The three independently-optional filters, AND-composed. */
export interface UsersFilterState {
  /** Matched case-insensitively against email OR name. */
  search: string;
  /** '' = no role filter active. */
  roleFilter: UserRole | '';
  /** Team entityId; '' = no team filter active. */
  teamFilter: string;
}

/** No filter active — the identity filter state. */
export const EMPTY_USERS_FILTERS: UsersFilterState = {
  search: '',
  roleFilter: '',
  teamFilter: '',
};

export type UserSortColumn = 'email' | 'name' | 'source' | 'lastLoggedInAt' | 'createdAt';
export type SortDir = 'asc' | 'desc';

/**
 * Apply the role + team + free-text filter to a user set. All three filters
 * are AND-composed — a user must pass every active filter (an empty
 * string/no role or team selected means that filter is inactive).
 */
export function filterUsers(
  users: PlatformUser[],
  filters: UsersFilterState,
  userTeamsMap: Map<string, Team[]>
): PlatformUser[] {
  const { search, roleFilter, teamFilter } = filters;
  const searchLower = search.trim().toLowerCase();

  return users.filter((user) => {
    if (roleFilter && !user.roles.includes(roleFilter)) return false;
    if (teamFilter) {
      const teams = userTeamsMap.get(user.userId) ?? [];
      if (!teams.some((team) => team.entityId === teamFilter)) return false;
    }
    if (!searchLower) return true;
    return (
      user.email.toLowerCase().includes(searchLower) ||
      user.name.toLowerCase().includes(searchLower)
    );
  });
}

/**
 * Sort a user set by column. Returns a new array — never mutates the input.
 * `lastLoggedInAt` is optional; missing values sort as the empty string
 * (never-logged-in users sort first ascending, last descending).
 * `createdAt` has no table header — it's the default ordering (issue #1233)
 * so a freshly created row lands on page 1 regardless of accumulated count.
 */
export function sortUsers(
  users: PlatformUser[],
  sortCol: UserSortColumn,
  sortDir: SortDir
): PlatformUser[] {
  const sorted = [...users].sort((a, b) => {
    let cmp: number;
    switch (sortCol) {
      case 'email':
        cmp = a.email.localeCompare(b.email);
        break;
      case 'name':
        cmp = a.name.localeCompare(b.name);
        break;
      case 'source':
        cmp = (a.source ?? 'manual').localeCompare(b.source ?? 'manual');
        break;
      case 'lastLoggedInAt':
        cmp = (a.lastLoggedInAt ?? '').localeCompare(b.lastLoggedInAt ?? '');
        break;
      case 'createdAt':
        // Defensive fallback (issue #1239) mirrors the `source` case above —
        // the type declares `_createdAt` as required, but legacy/seed
        // records can still omit it at runtime.
        cmp = (a._createdAt ?? '').localeCompare(b._createdAt ?? '');
        break;
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });
  return sorted;
}

/**
 * Sort teams alphabetically by name (case-insensitive via `localeCompare`).
 * Used by `AssignTeamDialog` for both the current-teams and
 * available-teams lists (issue #1224) and by the users-table team filter's
 * option list, so team ordering is consistent wherever teams are listed on
 * this page.
 */
export function sortTeamsByName<T extends { name: string }>(teams: T[]): T[] {
  return [...teams].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Build a `userId -> Team[]` lookup from every team's `memberIds` (issue
 * #1411 extraction from `UsersAdminPage`). Used to render each user's team
 * chips and to drive the team filter without a per-user query.
 */
export function buildUserTeamsMap(teams: Team[]): Map<string, Team[]> {
  const map = new Map<string, Team[]>();
  for (const team of teams) {
    for (const memberId of team.memberIds) {
      const existing = map.get(memberId) ?? [];
      map.set(memberId, [...existing, team]);
    }
  }
  return map;
}

/**
 * Render a relative "time ago" label for a user's last login, honoring the
 * same buckets the Admin Users page has always used (issue #1411 extraction
 * — no behavior change).
 */
export function formatLastLogin(iso: string | undefined): string {
  if (!iso) return 'Never';
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}
